import { IFsLprProviderInterface } from '../../../../../api';
import { ViLPRProvider } from '../../../../../shared/models';
import { extractCameraModelName } from '../../../../../utils';
import { LPRCameraModelsSelection } from '../../models';
import { AXON_VENDOR_NAME } from '../provider/lpr-provider-helpers';

export interface TotalResultsCalculationParams {
  providers: IFsLprProviderInterface[];
  sourcesSelection: Map<ViLPRProvider, boolean>;
  cameraModelSelections: Map<string, LPRCameraModelsSelection>;
  shouldShowPartialMatches: boolean;
  worksWithAxonCount: number;
}

/**
 * Calculates the total result count from all selected sources, accounting for
 * camera model exclusions.
 *
 * Axon VI and WWA share the same provider but are treated as separate sources:
 * - AxonVI's provider.total includes both Axon and WWA counts
 * - When only AxonVI is selected: provider.total - worksWithAxonCount
 * - When only WWA is selected: worksWithAxonCount
 * - When both are selected: provider.total (includes both)
 *
 * The final total subtracts counts for deselected camera models when their
 * provider is selected.
 *
 * @param params - Calculation parameters including providers, selections, and settings
 * @returns The total result count (never negative)
 */
export function calculateTotalResults(params: TotalResultsCalculationParams): number {
  const {
    providers,
    sourcesSelection,
    cameraModelSelections,
    shouldShowPartialMatches,
    worksWithAxonCount,
  } = params;

  // Wait for all providers to be stable (not loading) before calculating
  // This prevents showing stale counts when provider states are transitioning
  const anyProviderLoading = providers.some((provider) => provider.isLoading);
  if (anyProviderLoading) {
    return 0;
  }

  const isAxonVISelected = sourcesSelection.get(ViLPRProvider.AXON_VI) ?? false;
  const isWWASelected = sourcesSelection.get(ViLPRProvider.WORKS_WITH_AXON) ?? false;

  let total = 0;

  for (const provider of providers) {
    if ((provider.errors?.length ?? 0) > 0) {
      continue;
    }

    const providerEnum = provider.nameEnum ?? (provider.name as ViLPRProvider);

    if (providerEnum === ViLPRProvider.AXON_VI) {
      total += calculateAxonVITotal(
        provider,
        shouldShowPartialMatches,
        isAxonVISelected,
        isWWASelected,
        worksWithAxonCount,
      );
    } else {
      const isSelected = sourcesSelection.get(providerEnum) ?? false;
      if (isSelected) {
        total += getProviderTotal(provider, shouldShowPartialMatches);
      }
    }
  }

  const totalExclusions = calculateTotalExclusions({
    providers,
    sourcesSelection,
    cameraModelSelections,
    isAxonVISelected,
    isWWASelected,
  });

  return Math.max(0, total - totalExclusions);
}

/**
 * Calculates the total for Axon VI provider based on selection state.
 *
 * Handles the special case where Axon VI and WWA share the same provider:
 * - Both selected: return full provider total
 * - Only Axon VI: return provider total minus WWA count
 * - Only WWA: return WWA count only
 * - Neither: return 0
 *
 * @param provider - The Axon VI provider
 * @param shouldShowPartialMatches - Whether to include results with warnings
 * @param isAxonVISelected - Whether Axon VI source is selected
 * @param isWWASelected - Whether WWA source is selected
 * @param worksWithAxonCount - The count of WWA results
 * @returns The calculated total for Axon VI/WWA
 */
function calculateAxonVITotal(
  provider: IFsLprProviderInterface,
  shouldShowPartialMatches: boolean,
  isAxonVISelected: boolean,
  isWWASelected: boolean,
  worksWithAxonCount: number,
): number {
  const axonVITotal = getProviderTotal(provider, shouldShowPartialMatches);

  if (isAxonVISelected && isWWASelected) {
    return axonVITotal;
  }

  if (isAxonVISelected) {
    return Math.max(0, axonVITotal - worksWithAxonCount);
  }

  if (isWWASelected) {
    return worksWithAxonCount;
  }

  return 0;
}

/**
 * Calculates the total exclusion count from all camera model selections.
 *
 * Iterates through all camera model selections and sums up the exclusion counts
 * for deselected camera models.
 *
 * @param params - Parameters including providers, selections, and selection states
 * @returns The total count to exclude from the results
 */
function calculateTotalExclusions(params: {
  providers: IFsLprProviderInterface[];
  sourcesSelection: Map<ViLPRProvider, boolean>;
  cameraModelSelections: Map<string, LPRCameraModelsSelection>;
  isAxonVISelected: boolean;
  isWWASelected: boolean;
}): number {
  const { providers, sourcesSelection, cameraModelSelections, isAxonVISelected, isWWASelected } =
    params;

  let totalExclusions = 0;

  for (const cameraModelSelection of cameraModelSelections.values()) {
    totalExclusions += calculateCameraModelExclusions({
      providers,
      sourcesSelection,
      cameraModelSelection,
      isAxonVISelected,
      isWWASelected,
    });
  }

  return totalExclusions;
}

/**
 * Calculates exclusions for deselected camera models for a specific selection.
 *
 * Only subtracts counts for unselected camera models when their provider is selected.
 * If a provider is deselected, we don't subtract its camera model counts
 * (they're already excluded from the total).
 *
 * @param params - Parameters including providers, selections, and selection states
 * @returns The exclusion count for this camera model selection (0 if provider not selected)
 */
function calculateCameraModelExclusions(params: {
  providers: IFsLprProviderInterface[];
  sourcesSelection: Map<ViLPRProvider, boolean>;
  cameraModelSelection: LPRCameraModelsSelection;
  isAxonVISelected: boolean;
  isWWASelected: boolean;
}): number {
  const { providers, sourcesSelection, cameraModelSelection, isAxonVISelected, isWWASelected } =
    params;

  if (!cameraModelSelection.cameraModels?.size) {
    return 0;
  }

  const provider = findProviderForCameraModelSelection(
    providers,
    cameraModelSelection.providerName,
  );

  if (!provider) {
    return 0;
  }

  const { isProviderSelected, cameraModelFacets } = getCameraModelSelectionContext({
    provider,
    cameraModelSelection,
    sourcesSelection,
    isAxonVISelected,
    isWWASelected,
  });

  if (!isProviderSelected || !cameraModelFacets.length) {
    return 0;
  }

  return countDeselectedCameraModels(cameraModelFacets, cameraModelSelection);
}

/**
 * Finds the provider associated with a camera model selection.
 *
 * Handles the special case where both Axon VI and WWA camera models
 * reference the same AXON_VI provider.
 *
 * @param providers - Array of all providers
 * @param providerName - The provider name from the camera model selection
 * @returns The matching provider, or undefined if not found
 */
function findProviderForCameraModelSelection(
  providers: IFsLprProviderInterface[],
  providerName: ViLPRProvider,
): IFsLprProviderInterface | undefined {
  if (providerName === ViLPRProvider.WORKS_WITH_AXON || providerName === ViLPRProvider.AXON_VI) {
    return providers.find((p) => p.nameEnum === ViLPRProvider.AXON_VI);
  }

  return providers.find((p) => p.nameEnum === providerName);
}

/**
 * Determines the provider selection state and extracts relevant camera model facets.
 *
 * Differentiates between Axon VI camera models (cameraVendor === 'axon') and
 * WWA camera models (cameraVendor === undefined) to determine which provider
 * selection state to check and which facets to extract.
 *
 * @param params - Parameters including provider, camera model selection, and selection states
 * @returns An object containing whether the provider is selected and the relevant camera model facets
 */
function getCameraModelSelectionContext(params: {
  provider: IFsLprProviderInterface;
  cameraModelSelection: LPRCameraModelsSelection;
  sourcesSelection: Map<ViLPRProvider, boolean>;
  isAxonVISelected: boolean;
  isWWASelected: boolean;
}): { isProviderSelected: boolean; cameraModelFacets: Array<{ model: string; count: number }> } {
  const { provider, cameraModelSelection, sourcesSelection, isAxonVISelected, isWWASelected } =
    params;

  const { providerName, cameraVendor } = cameraModelSelection;

  const isAxonCameraModel = cameraVendor === AXON_VENDOR_NAME;
  const isWWACameraModel = !isAxonCameraModel && providerName === ViLPRProvider.AXON_VI;

  const isProviderSelected = isAxonCameraModel
    ? isAxonVISelected
    : isWWACameraModel
    ? isWWASelected
    : sourcesSelection.get(providerName) ?? false;

  const cameraModelFacets = extractCameraModelFacets(provider, isAxonCameraModel, isWWACameraModel);

  return { isProviderSelected, cameraModelFacets };
}

/**
 * Extracts camera model facets from a provider based on camera model type.
 *
 * The provider's facets contain camera vendors, where:
 * - vendors[0] is always the Axon vendor (for Axon VI camera models)
 * - vendors.slice(1) are non-Axon vendors (for WWA camera models)
 * - All vendors are used for other providers
 *
 * @param provider - The provider to extract facets from
 * @param isAxonCameraModel - Whether this is an Axon VI camera model
 * @param isWWACameraModel - Whether this is a WWA camera model
 * @returns Array of camera model facets with their counts
 */
function extractCameraModelFacets(
  provider: IFsLprProviderInterface,
  isAxonCameraModel: boolean,
  isWWACameraModel: boolean,
): Array<{ model: string; count: number }> {
  const vendors = provider.facets?.cameraVendors;

  if (!vendors || vendors.length === 0) {
    return [];
  }

  if (isAxonCameraModel) {
    return vendors[0]?.cameraModels || [];
  }

  if (isWWACameraModel) {
    return vendors.slice(1).flatMap((vendor) => vendor.cameraModels || []);
  }

  return vendors.flatMap((vendor) => vendor.cameraModels || []);
}

/**
 * Counts the total exclusion count for deselected camera models.
 *
 * Iterates through the camera model selection and sums up the counts
 * for all deselected camera models that match the provided facets.
 *
 * @param cameraModelFacets - Available camera model facets with counts
 * @param cameraModelSelection - The camera model selection state
 * @returns The total count to exclude for deselected camera models
 */
function countDeselectedCameraModels(
  cameraModelFacets: Array<{ model: string; count: number }>,
  cameraModelSelection: LPRCameraModelsSelection,
): number {
  let exclusionCount = 0;

  cameraModelSelection.cameraModels.forEach((isSelected, cameraModelInSelection) => {
    if (isSelected) {
      return;
    }

    const cameraModelName = extractCameraModelName(cameraModelInSelection);

    const cameraModelToExclude = cameraModelFacets.find(
      (facetsCamera) => facetsCamera.model === cameraModelName,
    );

    const countToExclude = cameraModelToExclude?.count ?? 0;

    exclusionCount += countToExclude;
  });

  return exclusionCount;
}

/**
 * Gets the total count for a provider, accounting for errors and warnings.
 *
 * - Providers with errors always return 0
 * - Providers with warnings return 0 unless partial matches should be shown
 * - Otherwise returns the provider's total count
 *
 * @param provider - The provider to get the total for
 * @param shouldShowPartialMatches - Whether to include results with warnings
 * @returns The provider's total count (0 if errors or warnings prevent it)
 */
function getProviderTotal(
  provider: IFsLprProviderInterface,
  shouldShowPartialMatches: boolean,
): number {
  if ((provider.errors?.length ?? 0) > 0) {
    return 0;
  }

  if ((provider.warnings?.length ?? 0) > 0) {
    return shouldShowPartialMatches ? provider.total ?? 0 : 0;
  }

  return provider.total ?? 0;
}
